import type React from 'react';
import { ArrowRight, BusFront, ChevronRight, Clock3, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useSchedules, useSchedulesActions } from './state';
import { routes, selectedDirection, selectedRoute } from './state/schedules.machine';

const localClock = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export function BusTimesPage() {
  const snapshot = useSchedules((current) => current);
  const actions = useSchedulesActions();
  const { routeId, directionId, stopId, arrivals, checkedAt, error } = snapshot.context;
  const route = selectedRoute(snapshot.context);
  const direction = selectedDirection(snapshot.context);
  const stop = direction?.stops.find((candidate) => candidate.id === stopId);
  const currentSeattle = localClock.format(new Date());

  return (
    <main className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8 sm:py-12">
        <header className="mb-10 flex flex-col gap-6 border-b border-border/80 pb-7 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="max-w-xl font-heading text-4xl font-semibold tracking-tight sm:text-5xl">
              Next buses, without the guesswork.
            </h1>
            <p className="mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
              A calm departure board for the RapidRide E Line and Route 44. Times are scheduled, not live vehicle
              locations.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start rounded-full border border-border bg-card px-3 py-2 text-xs text-muted-foreground sm:self-auto">
            <span className="size-2 rounded-full bg-primary" aria-hidden="true" /> {currentSeattle} PT
          </div>
        </header>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start">
          <section aria-labelledby="selection-heading">
            <div className="mb-4 flex items-end justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">01—03</p>
                <h2 id="selection-heading" className="mt-1 font-heading text-2xl font-semibold">
                  Choose your trip
                </h2>
              </div>
              {routeId && <span className="text-xs text-muted-foreground">Change any time</span>}
            </div>
            <div className="space-y-3">
              <SelectionPanel number="01" label="Route" summary={route?.name ?? 'Select a route'} active>
                <div className="grid gap-2 sm:grid-cols-2">
                  {routes.map((candidate) => (
                    <Button
                      key={candidate.id}
                      variant={routeId === candidate.id ? 'default' : 'outline'}
                      className="h-11 justify-start"
                      onClick={() => actions.chooseRoute(candidate.id)}
                      data-jr-key={candidate.id === 'E' ? 'choose-route-e' : 'choose-route-44'}
                    >
                      {candidate.name}
                    </Button>
                  ))}
                </div>
              </SelectionPanel>

              <SelectionPanel
                number="02"
                label="Direction"
                summary={direction?.name ?? (route ? 'Select a direction' : 'Waiting for route')}
                active={Boolean(route)}
              >
                <div className="grid gap-2">
                  {route?.directions.map((candidate, index) => (
                    <Button
                      key={candidate.id}
                      variant={directionId === candidate.id ? 'default' : 'outline'}
                      className="h-11 justify-start whitespace-normal text-left"
                      disabled={!route}
                      onClick={() => actions.chooseDirection(candidate.id)}
                      data-jr-key={index === 0 ? 'choose-direction' : `choose-direction-${candidate.id}`}
                    >
                      {candidate.name}
                    </Button>
                  ))}
                </div>
              </SelectionPanel>

              <SelectionPanel
                number="03"
                label="Stop"
                summary={stop?.name ?? (direction ? 'Select a stop' : 'Waiting for direction')}
                active={Boolean(direction)}
              >
                <div className="grid gap-2">
                  {direction?.stops.map((candidate, index) => (
                    <Button
                      key={candidate.id}
                      variant={stopId === candidate.id ? 'default' : 'outline'}
                      className="h-auto min-h-11 justify-start whitespace-normal py-2 text-left"
                      disabled={!direction}
                      onClick={() => actions.chooseStop(candidate.id)}
                      data-jr-key={index === 0 ? 'choose-stop' : `choose-stop-${candidate.id}`}
                    >
                      {candidate.name}
                    </Button>
                  ))}
                </div>
              </SelectionPanel>
            </div>
            <p className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
              <ChevronRight className="size-3" /> Your selection is remembered on this device.
            </p>
          </section>

          <section aria-labelledby="departures-heading" className="min-w-0">
            <Card className="overflow-hidden border-primary/20 bg-card shadow-lg">
              <div className="border-b border-border/70 bg-secondary/45 px-6 py-5 sm:px-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                      Scheduled departures
                    </p>
                    <h2 id="departures-heading" className="mt-1 font-heading text-2xl font-semibold">
                      {stop?.name ?? 'No stop selected'}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {route && direction ? `${route.name} · ${direction.name}` : 'Choose a stop to see the board'}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!stopId || !snapshot.can({ type: 'schedule.refresh-arrivals' })}
                    onClick={() => actions.refreshArrivals()}
                    data-jr-key="refresh-arrivals"
                  >
                    <RefreshCw className="size-3.5" /> Refresh
                  </Button>
                </div>
              </div>
              <CardContent className="p-6 sm:p-8">
                {error ? (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-5 text-sm text-destructive">
                    We couldn’t calculate departures right now. Try refreshing.
                  </div>
                ) : !stopId ? (
                  <EmptyBoard />
                ) : arrivals.length === 0 ? (
                  <div className="py-10 text-center">
                    <Clock3 className="mx-auto size-8 text-muted-foreground/60" />
                    <p className="mt-4 font-medium">No more scheduled departures today.</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Try another stop or check back after the service day rolls over.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {arrivals.map((arrival, index) => (
                      <ArrivalTile key={`${arrival.time}-${index}`} arrival={arrival} />
                    ))}
                  </div>
                )}
                {stopId && checkedAt && (
                  <p className="mt-6 border-t border-border/70 pt-4 text-xs text-muted-foreground">
                    Checked {localClock.format(new Date(checkedAt))} PT · Scheduled timetable
                  </p>
                )}
              </CardContent>
            </Card>
          </section>
        </div>
      </div>
    </main>
  );
}

function SelectionPanel({
  number,
  label,
  summary,
  active,
  children,
}: {
  number: string;
  label: string;
  summary: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${active ? 'border-primary/30 bg-card' : 'border-border/60 bg-muted/25 opacity-70'}`}
    >
      <div className="mb-3 flex items-start gap-3">
        <span className="font-mono text-xs font-semibold text-primary">{number}</span>
        <div className="min-w-0">
          <p className="text-sm font-semibold">{label}</p>
          <p className="truncate text-xs text-muted-foreground">{summary}</p>
        </div>
        <ArrowRight className="ml-auto mt-0.5 size-4 text-muted-foreground/60" />
      </div>
      {children}
    </div>
  );
}

function EmptyBoard() {
  return (
    <div className="py-12 text-center">
      <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-secondary text-primary">
        <BusFront className="size-7" />
      </div>
      <p className="mt-5 font-heading text-xl font-semibold">Your board is ready when you are.</p>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
        Choose a route, direction, and stop to see the next scheduled departures for Seattle.
      </p>
    </div>
  );
}

function ArrivalTile({ arrival }: { arrival: { time: string; minutes: number; source: 'scheduled' | 'missing' } }) {
  if (arrival.source === 'missing')
    return (
      <div className="rounded-lg border border-dashed border-border p-5">
        <p className="font-medium">Source time unavailable</p>
        <p className="mt-1 text-sm text-muted-foreground">
          This stop does not have a published scheduled departure in the supplied timetable.
        </p>
      </div>
    );
  return (
    <div className="flex items-center justify-between rounded-xl border border-border/70 bg-background p-4 sm:p-5">
      <div className="flex items-center gap-4">
        <span className="font-mono text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
          {arrival.time}
        </span>
        <Badge variant="secondary" className="hidden sm:inline-flex">
          Scheduled
        </Badge>
      </div>
      <div className="text-right">
        <p className="font-heading text-xl font-semibold text-primary">
          {arrival.minutes === 0 ? 'now' : `${arrival.minutes} min`}
        </p>
        <p className="text-xs text-muted-foreground">Seattle local</p>
      </div>
    </div>
  );
}
